import { useState, type FormEvent } from 'react';

interface CalculationResult {
  perServingMg: number;
  totalMg: number;
}

export function CalculatorPage() {
  const [materialWeight, setMaterialWeight] = useState('');
  const [cannabinoidPercent, setCannabinoidPercent] = useState('');
  const [decarboxylationEff, setDecarboxylationEff] = useState('88');
  const [extractionEff, setExtractionEff] = useState('70');
  const [servings, setServings] = useState('');
  const [result, setResult] = useState<CalculationResult | null>(null);

  const handleCalculate = (e: FormEvent) => {
    e.preventDefault();

    const weight = parseFloat(materialWeight);
    const percent = parseFloat(cannabinoidPercent);
    const decarb = parseFloat(decarboxylationEff) / 100;
    const extraction = parseFloat(extractionEff) / 100;
    const numServings = parseInt(servings, 10);

    if ([weight, percent, decarb, extraction, numServings].some(isNaN) || numServings === 0) {
      return;
    }

    const totalMg = weight * (percent / 100) * 1000 * decarb * extraction;
    const perServingMg = totalMg / numServings;

    setResult({ perServingMg, totalMg });
  };

  return (
    <div className="page calculator-page">
      <h2>Dosing Calculator</h2>
      <p className="page-description">
        Calculate precise per-serving cannabinoid content for your infused recipes.
      </p>

      <form onSubmit={handleCalculate} className="calculator-form">
        <div className="form-group">
          <label htmlFor="material-weight">Material Weight (grams)</label>
          <input
            id="material-weight"
            type="number"
            step="0.01"
            min="0"
            value={materialWeight}
            onChange={(e) => setMaterialWeight(e.target.value)}
            placeholder="e.g., 7"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="cannabinoid-percent">Cannabinoid Percentage (%)</label>
          <input
            id="cannabinoid-percent"
            type="number"
            step="0.1"
            min="0"
            max="100"
            value={cannabinoidPercent}
            onChange={(e) => setCannabinoidPercent(e.target.value)}
            placeholder="e.g., 20"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="decarb-eff">Decarboxylation Efficiency (%)</label>
          <input
            id="decarb-eff"
            type="number"
            step="1"
            min="0"
            max="100"
            value={decarboxylationEff}
            onChange={(e) => setDecarboxylationEff(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="extraction-eff">Extraction Efficiency (%)</label>
          <input
            id="extraction-eff"
            type="number"
            step="1"
            min="0"
            max="100"
            value={extractionEff}
            onChange={(e) => setExtractionEff(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="servings">Number of Servings</label>
          <input
            id="servings"
            type="number"
            step="1"
            min="1"
            value={servings}
            onChange={(e) => setServings(e.target.value)}
            placeholder="e.g., 12"
            required
          />
        </div>

        <button type="submit" className="calculate-button">
          Calculate Dosage
        </button>
      </form>

      {result && (
        <div className="result-card" role="region" aria-label="Calculation results">
          <h3>Results</h3>
          <div className="result-values">
            <div className="result-item">
              <span className="result-label">Per Serving</span>
              <span className="result-value">{result.perServingMg.toFixed(2)} mg</span>
            </div>
            <div className="result-item">
              <span className="result-label">Total in Recipe</span>
              <span className="result-value">{result.totalMg.toFixed(2)} mg</span>
            </div>
          </div>
          <p className="result-disclaimer">
            For informational purposes only. Actual potency may vary based on material quality,
            preparation method, and other factors.
          </p>
        </div>
      )}
    </div>
  );
}
